/* ============================================
   CronogramaPagos — Payment Schedule Editor
   Editable cuota rows with auto-calculated
   amounts from percentage * total
   ============================================ */
import type { CuotaPago } from '@/lib/types'
import { r2, formatCurrency } from '@/lib/utils'
import { IconTrash, IconPlus, IconAlertTriangle } from '@/components/icons/Icons'

interface CronogramaPagosProps {
  cuotas: CuotaPago[]
  total: number
  moneda: string
  onUpdate: (cuotas: CuotaPago[]) => void
}

export default function CronogramaPagos({ cuotas, total, moneda, onUpdate }: CronogramaPagosProps) {
  const totalPct = r2(cuotas.reduce((s, c) => s + c.porcentaje, 0))
  const totalMonto = r2(cuotas.reduce((s, c) => s + c.monto, 0))
  const isBalanced = Math.abs(totalPct - 100) < 0.01

  const updateRow = (idx: number, field: keyof CuotaPago, value: string | number) => {
    onUpdate(cuotas.map((c, i) => {
      if (i !== idx) return c
      const updated = { ...c }
      if (field === 'porcentaje') {
        updated.porcentaje = parseFloat(String(value)) || 0
        updated.monto = r2(total * updated.porcentaje / 100)
      } else if (field === 'descripcion' || field === 'condicion') {
        (updated as Record<string, unknown>)[field] = value
      }
      return updated
    }))
  }

  const removeRow = (idx: number) => {
    const filtered = cuotas.filter((_, i) => i !== idx)
    // Renumber
    onUpdate(filtered.map((c, i) => ({ ...c, numero: i + 1 })))
  }

  const addRow = () => {
    const nextNum = cuotas.length + 1
    onUpdate([...cuotas, {
      numero: nextNum,
      descripcion: `Cuota ${nextNum}`,
      porcentaje: 0,
      monto: 0,
      condicion: '',
    }])
  }

  return (
    <div className="cronograma-editor">
      <div className="cronograma-editor__header">
        <h4 className="cronograma-editor__title">Cronograma de Pagos</h4>
        <span className="cronograma-editor__count">{cuotas.length} cuota{cuotas.length !== 1 ? 's' : ''}</span>
      </div>

      {cuotas.length === 0 ? (
        <p className="cronograma-editor__empty">Sin cuotas definidas</p>
      ) : (
        <div className="cronograma-editor__list">
          <div className="cronograma-editor__row cronograma-editor__row--header">
            <span>#</span>
            <span>Descripcion</span>
            <span>%</span>
            <span>Monto</span>
            <span>Condicion</span>
            <span></span>
          </div>
          {cuotas.map((c, i) => (
            <div className="cronograma-editor__row" key={i}>
              <span className="cronograma-editor__num">{c.numero}</span>
              <input
                className="form-input"
                value={c.descripcion}
                onChange={e => updateRow(i, 'descripcion', e.target.value)}
                placeholder="Ej: Anticipo"
              />
              <input
                className="form-input"
                type="number"
                step="0.01"
                value={c.porcentaje}
                onChange={e => updateRow(i, 'porcentaje', e.target.value)}
              />
              <span className="cronograma-editor__monto">
                {formatCurrency(c.monto, moneda)}
              </span>
              <input
                className="form-input"
                value={c.condicion}
                onChange={e => updateRow(i, 'condicion', e.target.value)}
                placeholder="Ej: Al inicio"
              />
              <button className="cronograma-editor__remove" onClick={() => removeRow(i)} type="button">
                <IconTrash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <button className="btn btn-secondary btn-sm cronograma-editor__add" onClick={addRow} type="button">
        <IconPlus size={14} /> Agregar cuota
      </button>

      {cuotas.length > 0 && (
        <div className="cronograma-editor__footer">
          <div className="cronograma-editor__footer-row">
            <span>Total</span>
            <span className={`cronograma-editor__pct-badge ${isBalanced ? 'cronograma-editor__pct-badge--ok' : 'cronograma-editor__pct-badge--warn'}`}>
              {totalPct.toFixed(1)}%
            </span>
            <strong>{formatCurrency(totalMonto, moneda)}</strong>
          </div>
          {!isBalanced && (
            <div className="cronograma-editor__warning">
              <IconAlertTriangle size={14} />
              <span>El porcentaje total no suma 100%</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
